/** Arama ekranı: gruplu sonuçlar, tür/izlenme filtreleri ve keşif yönlendirmeleri. */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { api, type Item, type Library } from '../api.ts';
import { ItemCard } from '../components/ItemCard.tsx';
import { ErrorNotice, Loading } from '../components/States.tsx';
import { filterSearchHits, groupSearchHits, type SearchGroupKey } from '../searchResults.ts';

const GROUP_LIMIT = 6;

export function SearchScreen({
  query,
  onWatch,
  onOpenItem,
  onBack,
  libraries = null,
}: {
  query: string;
  onWatch: (itemId: string) => void;
  onOpenItem?: (itemId: string) => void;
  /** Boş/filtresiz durumdan çıkış; kaynak ekrana döner. */
  onBack?: (() => void) | undefined;
  libraries?: Library[] | null;
}): ReactNode {
  const [hits, setHits] = useState<Item[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [kindFilter, setKindFilter] = useState<'all' | SearchGroupKey>('all');
  const [unwatchedOnly, setUnwatchedOnly] = useState(false);
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [total, setTotal] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<SearchGroupKey>>(new Set());

  const trimmedQuery = query.trim();
  const libraryList = libraries ?? [];
  const musicLibraryIds = new Set(
    libraryList.filter((library) => library.kind === 'music').map((library) => library.id),
  );

  // Sorgu değişince sonuçları tazele. `key={query}` kabukta da var; burada
  // yine de bağımlılık açıkça tutulur.
  useEffect(() => {
    // `reloadKey` yalnız aynı sorguyu yeniden denemek için; efektte okunur.
    void reloadKey;
    setError(null);
    setKindFilter('all');
    setUnwatchedOnly(false);
    setExpandedGroups(new Set());
    const trimmed = query.trim();
    if (trimmed.length === 0) {
      setHits([]);
      return;
    }
    setHits(null);
    let cancelled = false;
    const from = /^\d{4}$/.test(yearFrom) ? Number(yearFrom) : null;
    const to = /^\d{4}$/.test(yearTo) ? Number(yearTo) : null;
    void api
      .search(trimmed, { yearFrom: from, yearTo: to })
      .then((found) => {
        if (!cancelled) {
          setHits(found.hits);
          setTotal(found.total);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : 'Arama başarısız.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [query, reloadKey, yearFrom, yearTo]);

  if (error) {
    return <ErrorNotice message={error} onRetry={() => setReloadKey((value) => value + 1)} />;
  }
  if (hits === null) return <Loading label={`“${trimmedQuery}” aranıyor…`} />;

  if (trimmedQuery.length === 0) {
    return <SearchLanding onBack={onBack} />;
  }

  if (hits.length === 0) {
    return (
      <NoResults
        query={trimmedQuery}
        onBack={onBack}
        onClearFilters={
          yearFrom.length > 0 || yearTo.length > 0
            ? () => {
                setYearFrom('');
                setYearTo('');
              }
            : undefined
        }
      />
    );
  }

  const groups = groupSearchHits(hits, musicLibraryIds);
  const visibleGroups = groups
    .filter((group) => kindFilter === 'all' || group.key === kindFilter)
    .map((group) => ({
      ...group,
      items: filterSearchHits(group.items, {
        kind: 'all',
        unwatchedOnly,
        musicLibraryIds,
      }),
    }))
    .filter((group) => group.items.length > 0);
  const visibleCount = visibleGroups.reduce((sum, group) => sum + group.items.length, 0);
  const resultCount = kindFilter === 'all' && !unwatchedOnly ? total : visibleCount;
  const filtersActive =
    kindFilter !== 'all' || unwatchedOnly || yearFrom.length > 0 || yearTo.length > 0;

  const resetFilters = (): void => {
    setKindFilter('all');
    setUnwatchedOnly(false);
    setYearFrom('');
    setYearTo('');
  };

  return (
    <div className="search-screen">
      <div className="search-screen__toolbar">
        <p className="search-screen__count" aria-live="polite">
          “{trimmedQuery}” için {resultCount} sonuç
          {filtersActive ? ' (filtreli)' : ''}
        </p>

        <fieldset className="search-screen__filters" aria-label="Sonuç türü">
          <button
            type="button"
            className="filter-chip"
            aria-pressed={kindFilter === 'all'}
            onClick={() => setKindFilter('all')}
          >
            Tümü <span className="filter-chip__count">{hits.length}</span>
          </button>
          {groups.map((group) => (
            <button
              key={group.key}
              type="button"
              className="filter-chip"
              aria-pressed={kindFilter === group.key}
              onClick={() => setKindFilter(group.key)}
            >
              {group.label} <span className="filter-chip__count">{group.items.length}</span>
            </button>
          ))}
        </fieldset>

        <fieldset className="search-screen__years">
          <legend className="visually-hidden">Yıl aralığı</legend>
          <label>
            Yıl
            <input
              className="search-screen__year"
              inputMode="numeric"
              maxLength={4}
              placeholder="Başlangıç"
              value={yearFrom}
              onChange={(event) => setYearFrom(event.target.value.replace(/\D/g, ''))}
            />
          </label>
          <span aria-hidden="true">–</span>
          <label>
            <span className="visually-hidden">Bitiş yılı</span>
            <input
              className="search-screen__year"
              inputMode="numeric"
              maxLength={4}
              placeholder="Bitiş"
              value={yearTo}
              onChange={(event) => setYearTo(event.target.value.replace(/\D/g, ''))}
            />
          </label>
        </fieldset>

        <label className="search-screen__toggle">
          <input
            type="checkbox"
            checked={unwatchedOnly}
            onChange={(event) => setUnwatchedOnly(event.target.checked)}
          />
          Yalnız izlenmemiş
        </label>
      </div>

      {visibleCount === 0 ? (
        <div className="search-screen__empty" role="status">
          <p>Bu filtrelerle eşleşen içerik yok.</p>
          <button type="button" className="button button--ghost" onClick={resetFilters}>
            Filtreleri temizle
          </button>
        </div>
      ) : (
        visibleGroups.map((group) => {
          const isExpanded = kindFilter !== 'all' || expandedGroups.has(group.key);
          const shown = isExpanded ? group.items : group.items.slice(0, GROUP_LIMIT);
          const hiddenCount = group.items.length - shown.length;
          return (
            <section
              key={group.key}
              className="search-screen__group"
              aria-labelledby={`search-group-${group.key}`}
            >
              <div className="section__header">
                <h2 id={`search-group-${group.key}`} className="section__title">
                  {group.label}{' '}
                  <span className="search-screen__group-count">{group.items.length}</span>
                </h2>
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className="button button--ghost search-screen__more"
                    onClick={() =>
                      setExpandedGroups((previous) => {
                        const next = new Set(previous);
                        if (next.has(group.key)) next.delete(group.key);
                        else next.add(group.key);
                        return next;
                      })
                    }
                  >
                    {isExpanded ? 'Daralt' : `Tümünü gör (${group.items.length})`}
                  </button>
                )}
              </div>

              <div className="grid">
                {shown.map((hit) => {
                  const isPlayable = hit.kind === 'movie' || hit.kind === 'episode';
                  return (
                    <ItemCard
                      key={hit.id}
                      item={hit}
                      mediaKind={group.key === 'music' ? 'music' : 'video'}
                      href={isPlayable ? `#/izle/${hit.id}` : `#/oge/${hit.id}`}
                      onClick={
                        isPlayable
                          ? () => onWatch(hit.id)
                          : onOpenItem
                            ? () => onOpenItem(hit.id)
                            : undefined
                      }
                    />
                  );
                })}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}

function NoResults({
  query,
  onBack,
  onClearFilters,
}: {
  query: string;
  onBack?: (() => void) | undefined;
  onClearFilters?: (() => void) | undefined;
}): ReactNode {
  return (
    <div className="search-screen__empty search-screen__empty--page">
      <h2>“{query}” için sonuç bulunamadı</h2>
      <p>Yazımı kontrol edin veya daha genel bir terim deneyin.</p>
      <div className="search-screen__empty-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={() => document.querySelector<HTMLInputElement>('input[type="search"]')?.focus()}
        >
          Aramayı değiştir
        </button>
        {onClearFilters && (
          <button type="button" className="button button--ghost" onClick={onClearFilters}>
            Filtreleri temizle
          </button>
        )}
        {onBack && (
          <button type="button" className="button button--ghost" onClick={onBack}>
            Geri dön
          </button>
        )}
      </div>
    </div>
  );
}

function SearchLanding({ onBack }: { onBack?: (() => void) | undefined }): ReactNode {
  useEffect(() => {
    // "Ara" sekmesi boş sorguyla açılır; kullanıcıyı doğrudan arama kutusuna
    // götürmek ek bir dokunma gerektirmez.
    document.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
  }, []);

  return (
    <div className="search-screen__empty search-screen__empty--page">
      <h2>Aramaya başlayın</h2>
      <p>Film, dizi, bölüm veya müzik aramak için üstteki arama kutusunu kullanın.</p>
      {onBack && (
        <button type="button" className="button button--ghost" onClick={onBack}>
          Geri dön
        </button>
      )}
    </div>
  );
}
